//@ts-check
import { renderAllSections } from './js-utils-rendersection.js'
import {
  readB2bVariantPricing,
  snapQuantityToRule,
  updateB2bQuantityPricing,
} from './js-utils-b2b-quantity-pricing.js'

class CUpdateQuantity extends HTMLElement {
  constructor() {
    super()
    this.productId = this.getAttribute('product-id')

    /** @type {HTMLInputElement|null} */
    this.input = null
    /** @type {HTMLButtonElement|null} */
    this.btnPlus = null
    /** @type {HTMLButtonElement|null} */
    this.btnMinus = null
    /** @type {HTMLInputElement[]|null} */
    this.productFormQuantity = null
    this.type = this.getAttribute('data-type') || null
    this.sectionsRender = [
      {
        title: 'header',
        selector: ['[data-render-icon-cart]'],
      },
      {
        title: 'cart-drawer',
        selector: ['[data-render-total]', '[data-render-item-price]'],
      },
    ]
  }

  /**
   * @returns {boolean}
   */
  isEditable() {
    return this.input != null && !this.input.readOnly
  }

  /**
   * B2B pricing config applies on product pages only, not in the cart drawer.
   * @returns {import('./js-utils-b2b-quantity-pricing.js').B2bVariantPricing|null}
   */
  getB2bConfig() {
    if (this.type === 'cart') return null
    return readB2bVariantPricing()
  }

  /**
   * @returns {number}
   */
  getQuantityIncrement() {
    const config = this.getB2bConfig()
    if (config) return config.quantityRule.increment ?? 1

    const increment = parseInt(this.input?.dataset.quantityIncrement ?? '1', 10)
    return Number.isNaN(increment) || increment < 1 ? 1 : increment
  }

  /**
   * @returns {number}
   */
  getQuantityMinimum() {
    const config = this.getB2bConfig()
    if (config) return config.quantityRule.min ?? 1

    const minimum = parseInt(this.input?.min ?? '1', 10)
    return Number.isNaN(minimum) || minimum < 1 ? 1 : minimum
  }

  /**
   * Strips non-digit characters from B2B quantity input.
   */
  sanitizeInput() {
    if (!this.input || !this.isEditable()) return

    const sanitized = this.input.value.replace(/\D/g, '')
    if (this.input.value !== sanitized) {
      this.input.value = sanitized
    }
  }

  /**
   * Ensures quantity is a positive integer and syncs related inputs.
   * @param {boolean} [triggerCartUpdate]
   * @param {Event} [event]
   */
  validateAndSync(triggerCartUpdate = false, event) {
    if (!this.input) return

    const config = this.getB2bConfig()

    if (config || this.isEditable()) {
      const minimum = this.getQuantityMinimum()
      let value = parseInt(this.input.value, 10)

      if (config) {
        value = snapQuantityToRule(
          Number.isNaN(value) ? minimum : value,
          config.quantityRule
        )
      } else if (Number.isNaN(value) || value < minimum) {
        value = minimum
      }

      this.input.value = String(value)

      this.productFormQuantity = Array.from(
        document.querySelectorAll('[data-product-form-quantity]')
      )
      if (this.productFormQuantity) {
        this.productFormQuantity.forEach((el) => (el.value = String(value)))
      }

      if (config) {
        updateB2bQuantityPricing(value)
      }
    }

    const minimum = this.getQuantityMinimum()
    const currentValue = parseInt(this.input.value, 10)
    if (this.btnMinus) {
      this.btnMinus.disabled =
        !Number.isFinite(currentValue) || currentValue <= minimum
    }

    if (triggerCartUpdate && this.type == 'cart') {
      this.handleUpdateCart(event)
    }
  }

  connectedCallback() {
    this.input = this.querySelector('[data-input-quantity]')
    this.btnPlus = this.querySelector('[data-btn-plus]')
    this.btnMinus = this.querySelector('[data-btn-minus]')

    if (this.dataset.quantityBound === 'true') return
    this.dataset.quantityBound = 'true'

    const minimum = this.getQuantityMinimum()
    const increment = this.getQuantityIncrement()

    this.btnPlus?.addEventListener('click', (e) => {
      if (!this.input) return
      const currentValue = parseInt(this.input.value, 10)
      this.input.value = String(currentValue + increment)
      this.btnMinus.disabled = false
      this.validateAndSync(this.type == 'cart', e)
    })
    this.btnMinus?.addEventListener('click', (e) => {
      if (!this.input) return
      const currentValue = parseInt(this.input.value, 10)
      const nextValue = currentValue - increment
      if (nextValue <= minimum) {
        this.input.value = String(minimum)
        this.btnMinus.disabled = true
      } else {
        this.btnMinus.disabled = false
        this.input.value = String(nextValue)
      }
      this.validateAndSync(this.type == 'cart', e)
    })

    this.input?.addEventListener('keydown', (e) => {
      if (!this.isEditable()) return

      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
      ]
      if (allowedKeys.includes(e.key)) return
      if (e.ctrlKey || e.metaKey) return
      if (!/^\d$/.test(e.key)) {
        e.preventDefault()
      }
    })

    this.input?.addEventListener('input', () => {
      this.sanitizeInput()
      if (this.isEditable()) {
        this.validateAndSync()
      }
    })

    this.input?.addEventListener('change', (e) => {
      this.validateAndSync(this.type == 'cart', e)
    })

    this.input?.addEventListener('blur', (e) => {
      this.validateAndSync(this.type == 'cart', e)
    })

    if (this.getB2bConfig() || this.isEditable()) {
      this.validateAndSync()
    }
  }

  handleUpdateCart(e) {
    e?.preventDefault()
    if (!this.input) return

    const formData = new FormData()
    formData.append(`updates[${this.productId}]`, this.input.value)
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return response.json()
      })
      .catch((error) => {
        console.error('Error:', error)
      })
      .finally(() => {
        renderAllSections(this.sectionsRender)
      })
  }

  disconnectedCallback() {}
}
if (!customElements.get('c-update-quantity')) {
  customElements.define('c-update-quantity', CUpdateQuantity)
}
